/**
 * Mathematical utility functions
 * Extends and improves upon the original Utils.js
 */
export default class MathUtils {
  // Constants
  static DEG_TO_RAD = Math.PI / 180;
  static RAD_TO_DEG = 180 / Math.PI;

  /**
   * Normalize an angle to 0-360 degrees
   * @param {number} angle - The angle to normalize
   * @return {number} Normalized angle
   */
  static normalizeAngle(angle) {
    return ((angle % 360) + 360) % 360;
  }

  /**
   * Get the quadrant (0-3) for a given angle
   * @param {number} angle - The angle in degrees
   * @return {number} Quadrant (0-3)
   */
  static getQuadrant(angle) {
    const normalized = this.normalizeAngle(angle);
    return Math.floor(normalized / 90);
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees - Angle in degrees
   * @return {number} Angle in radians
   */
  static toRadians(degrees) {
    return degrees * this.DEG_TO_RAD;
  }

  /**
   * Convert radians to degrees
   * @param {number} radians - Angle in radians
   * @return {number} Angle in degrees
   */
  static toDegrees(radians) {
    return radians * this.RAD_TO_DEG;
  }

  /**
   * Clamp a value between min and max
   * @param {number} value - The value to clamp
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @return {number} Clamped value
   */
  static clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Linear interpolation between two values
   * @param {number} a - Start value
   * @param {number} b - End value
   * @param {number} t - Interpolation factor (0-1)
   * @return {number} Interpolated value
   */
  static lerp(a, b, t) {
    return a + (b - a) * t;
  }

  /**
   * Smooth interpolation (smoothstep function)
   * @param {number} edge0 - Lower edge
   * @param {number} edge1 - Upper edge
   * @param {number} x - Value to interpolate
   * @return {number} Smoothly interpolated value (0-1)
   */
  static smoothstep(edge0, edge1, x) {
    const t = this.clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  }

  /**
   * Check if a value is a power of two
   * @param {number} value - Value to check
   * @return {boolean} True if power of two
   */
  static isPowerOfTwo(value) {
    return (value & (value - 1)) === 0 && value !== 0;
  }

  /**
   * Get next power of two greater than or equal to value
   * @param {number} value - Input value
   * @return {number} Next power of two
   */
  static nextPowerOfTwo(value) {
    value--;
    value |= value >> 1;
    value |= value >> 2;
    value |= value >> 4;
    value |= value >> 8;
    value |= value >> 16;
    value++;
    return value;
  }

  /**
   * Map a value from one range to another
   * @param {number} value - Value to map
   * @param {number} inMin - Input range minimum
   * @param {number} inMax - Input range maximum
   * @param {number} outMin - Output range minimum
   * @param {number} outMax - Output range maximum
   * @return {number} Mapped value
   */
  static map(value, inMin, inMax, outMin, outMax) {
    return outMin + (value - inMin) * (outMax - outMin) / (inMax - inMin);
  }

  /**
   * Check if two values are approximately equal (within epsilon)
   * @param {number} a - First value
   * @param {number} b - Second value
   * @param {number} epsilon - Tolerance (default 0.000001)
   * @return {boolean} True if approximately equal
   */
  static approximately(a, b, epsilon = 0.000001) {
    return Math.abs(a - b) < epsilon;
  }

  /**
   * Round a number to specified decimal places
   * @param {number} value - Value to round
   * @param {number} decimals - Number of decimal places
   * @return {number} Rounded value
   */
  static roundTo(value, decimals) {
    const multiplier = Math.pow(10, decimals);
    return Math.round(value * multiplier) / multiplier;
  }

  /**
   * Sign function (-1, 0, or 1)
   * @param {number} value - Input value
   * @return {number} Sign (-1, 0, or 1)
   */
  static sign(value) {
    return value > 0 ? 1 : value < 0 ? -1 : 0;
  }
}
